import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { SelectGridEmphasis, SelectGridOption } from '../select-grid/select-grid.types';
import * as moment_ from 'moment';
import { rotate } from '../../helpers/helpers';

const moment = moment_;
let $id = 0;

export enum DatePickerView {
  years = 'years',
  year = 'year',
  month = 'month'
}

export enum InputMode {
  value = 'value',
  start = 'start',
  end = 'end'
}

/**
 *  weekdays from Sunday (0) through Saturday (6)
 */
export declare type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

@Component({
  selector: 'wfm-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss']
})
export class DatePickerComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() id: number;

  @Input() value: moment_.Moment = undefined;
  @Output() valueChange = new EventEmitter<moment_.Moment>();

  @Input() startValue: moment_.Moment;
  @Output() startValueChange = new EventEmitter<moment_.Moment>();

  @Input() endValue: moment_.Moment;
  @Output() endValueChange = new EventEmitter<moment_.Moment>();

  @Input() disabled: boolean;

  @Input() error: boolean;
  @Output() errorChange = new EventEmitter<boolean>();

  @Input() startError: boolean;
  @Output() startErrorChange = new EventEmitter<boolean>();

  @Input() endError: boolean;
  @Output() endErrorChange = new EventEmitter<boolean>();

  @Input() rangeMode: boolean;

  @Input() hideOtherMonths: boolean;

  @Input() format = 'MM-DD-YYYY';
  @Input() formatDelimiter = '-';

  @Input() placeholder = '';

  @Input() min: moment_.Moment;

  @Input() max: moment_.Moment;

  @Input() autoWidth: boolean;

  @Input() filter: (date: moment_.Moment) => boolean;

  @Input() startDateLabel: string;
  @Input() endDateLabel: string;

  @Input() compactMode: boolean;

  @Input() instructionalText: string;

  @Input() inline = false;

  /**
   * weekdays from Sunday (0) through Saturday (6)
   * default = Sunday (0)
   */
  @Input() firstDayOfWeek: Weekday = undefined;

  private get $firstDayOfWeek(): Weekday {
    if (this.firstDayOfWeek !== undefined) return this.firstDayOfWeek;
    return moment.localeData().firstDayOfWeek() as Weekday;
  }

  input: string;
  startInput: string;
  endInput: string;

  inputMode: InputMode;
  inputModes = InputMode;

  current: moment_.Moment;
  startCurrent: moment_.Moment;
  endCurrent: moment_.Moment;

  today: moment_.Moment;

  isOpen = false;

  view: DatePickerView = DatePickerView.month;
  startView: DatePickerView = DatePickerView.month;
  endView: DatePickerView = DatePickerView.month;

  yearsGrid: SelectGridOption[] = [];
  startYearsGrid: SelectGridOption[] = [];
  endYearsGrid: SelectGridOption[] = [];

  daysGrid: SelectGridOption[] = [];
  startDaysGrid: SelectGridOption[] = [];
  endDaysGrid: SelectGridOption[] = [];

  private $monthsGrid: SelectGridOption[] = [];
  private $startMonthsGrid: SelectGridOption[] = [];
  private $endMonthsGrid: SelectGridOption[] = [];
  private inputStrLen: number;

  //months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  clearInput() {
    this.startInput = '';
    this.endInput = '';
    this.startError = false;
    this.endError = false;
    this.disabled = false;
    this.error = false;
    this.startDateInput?.nativeElement?.focus();
  }

  get weekdaysMin(): string[] {
    return rotate(moment.weekdaysMin(), this.$firstDayOfWeek);
  }

  get monthsShort(): string[] {
    return moment.monthsShort();
  }

  get months(): string[] {
    return moment.months();
  }

  get monthsGrid(): SelectGridOption[] {
    return this.getMonthsGrid();
  }

  get startMonthsGrid(): SelectGridOption[] {
    return this.getMonthsGrid(InputMode.start);
  }

  get endMonthsGrid(): SelectGridOption[] {
    return this.getMonthsGrid(InputMode.end);
  }

  @ViewChild('datepicker') element: ElementRef;
  @ViewChild('startDateInput') startDateInput: ElementRef<HTMLElement>;

  private window: Window;
  private mouseUpEvent: () => void;

  constructor(@Inject(DOCUMENT) private document, private renderer: Renderer2) {
    this.window = this.document.defaultView;

    this.id = ++$id;

    this.$monthsGrid = this.initMonthGrid();
    this.$startMonthsGrid = this.initMonthGrid();
    this.$endMonthsGrid = this.initMonthGrid();
  }

  private initMonthGrid() {
    const monthGrid: SelectGridOption[] = [];
    monthGrid.length = 12;
    monthGrid.fill(null);
    return monthGrid.map((v, i) => ({
      label: this.monthsShort[i],
      selected: false,
      disabled: false,
      emphasis: SelectGridEmphasis.normal,
      index: i
    }));
  }

  private getMonthsGrid(inputMode?: InputMode): SelectGridOption[] {
    let monthsGrid = this.$monthsGrid;
    if (inputMode === InputMode.start) {
      monthsGrid = this.$startMonthsGrid;
    }
    if (inputMode === InputMode.end) {
      monthsGrid = this.$endMonthsGrid;
    }
    if (monthsGrid[0]?.label !== this.monthsShort[0]) {
      this.setMonthsGridLabels(monthsGrid);
    }
    return monthsGrid;
  }

  private setMonthsGridLabels(monthsGrid: SelectGridOption[]) {
    for (const month of monthsGrid) {
      month.label = this.monthsShort[month.index];
    }
  }

  ngAfterViewInit(): void {
    const focusTimeout = setTimeout(() => {
      this.startDateInput?.nativeElement?.focus();
      clearTimeout(focusTimeout);
    }, 0);
  }

  private setInputStrLength() {
    const splitFormat: string[] = this.format.split(this.formatDelimiter);
    this.inputStrLen = this.format.length - (splitFormat.length > 1 ? splitFormat.length - 1 : 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.format || changes.formatDelimiter) {
      this.setInputStrLength();
    }

    if (changes.value) {
      const error = this.error;

      this.error =
        !!(this.value && (!this.value.isValid() || !this.isInMinMaxDateLimits(this.value))) || this.value === null;
      if (!this.error) {
        this.input = this.value?.format(this.format) || '';
      }

      this.setCurrentDate();

      this.error !== error && this.errorChange.emit(this.error);
    }

    if (changes.startValue || changes.endValue) {
      this.startInput = (this.startValue?.format && this.startValue.format(this.format)) || this.startInput || '';
      this.endInput = (this.endValue?.format && this.endValue.format(this.format)) || this.endInput || '';

      this.setCurrentDate();
      this.initGrids();
      this.validateRange();
    }

    if (changes.disabled && !this.disabled && this.rangeMode) {
      this.validateRange();
    }

    if (changes.rangeMode || changes.autoWidth) {
      this.rangeContainerClasses = this._getRangeContainerClasses();
      this.rangeStartClasses = this._getRangeStartClasses();
      this.rangeStartInputClasses = this._getRangeStartInputClasses();
      this.rangeEndClasses = this._getRangeEndClasses();
      this.rangeEndInputClasses = this._getRangeEndInputClasses();
      this.valueInputClasses = this._getValueInputClasses();
    }
  }

  ngOnInit(): void {
    this.today = moment(moment().format('YYYY-MM-DD'));

    this.setCurrentDate();

    this.initGrids();

    if (this.value && this.value.isValid()) {
      this.input = this.value.format(this.format);
    }

    if (this.startValue && this.startValue.isValid()) {
      this.startInput = this.startValue.format(this.format);
    }

    if (this.endValue && this.endValue.isValid()) {
      this.endInput = this.endValue.format(this.format);
    }
  }

  initGrids() {
    this.today = moment(moment().format('YYYY-MM-DD'));

    this.setYearsGrid();
    this.setYearsGrid(InputMode.start);
    this.setYearsGrid(InputMode.end);

    this.setMonthsGrid();
    this.setMonthsGrid(InputMode.start);
    this.setMonthsGrid(InputMode.end);

    this.setDaysGrid();
    this.setDaysGrid(InputMode.start);
    this.setDaysGrid(InputMode.end);
  }

  setCurrentDate() {
    this.current = (this.value && moment(this.value.format('YYYY-MM-DD'))) || moment(this.today);
    this.startCurrent =
      (this.startValue?.isValid() && moment(this.startValue.format('YYYY-MM-DD'))) || moment(this.today);
    this.endCurrent = (this.endValue?.isValid() && moment(this.endValue.format('YYYY-MM-DD'))) || moment(this.today);

    if (this.max && moment(this.current.format('YYYY-MM-DD')).isAfter(this.max.format('YYYY-MM-DD'))) {
      this.current = moment(this.max);
    }
    if (this.min && moment(this.current.format('YYYY-MM-DD')).isBefore(this.min.format('YYYY-MM-DD'))) {
      this.current = moment(this.min);
    }

    if (this.max && moment(this.startCurrent.format('YYYY-MM-DD')).isAfter(this.max.format('YYYY-MM-DD'))) {
      this.startCurrent = moment(this.max);
    }
    if (this.min && moment(this.startCurrent.format('YYYY-MM-DD')).isBefore(this.min.format('YYYY-MM-DD'))) {
      this.startCurrent = moment(this.min);
    }

    if (this.max && moment(this.endCurrent.format('YYYY-MM-DD')).isAfter(this.max.format('YYYY-MM-DD'))) {
      this.endCurrent = moment(this.max);
    }
    if (this.min && moment(this.endCurrent.format('YYYY-MM-DD')).isBefore(this.min.format('YYYY-MM-DD'))) {
      this.endCurrent = moment(this.min);
    }
  }

  setInputMode(mode: InputMode): void {
    this.inputMode = mode;
  }

  goLeft(inputMode?: InputMode): void {
    let view = this.view;
    let current = this.current;

    if (inputMode && inputMode !== InputMode.value) {
      view = this[`${inputMode}View`];
      current = this[`${inputMode}Current`];
    }

    if (view === DatePickerView.month) {
      current.subtract(1, 'month');
      this.setDaysGrid(inputMode);
    } else if (view === DatePickerView.year) {
      current.subtract(1, 'year');
      this.setMonthsGrid(inputMode);
    } else {
      current.subtract(20, 'year');
      this.setYearsGrid(inputMode);
    }
  }

  goRight(inputMode?: InputMode): void {
    let view = this.view;
    let current = this.current;

    if (inputMode && inputMode !== InputMode.value) {
      view = this[`${inputMode}View`];
      current = this[`${inputMode}Current`];
    }

    if (view === DatePickerView.month) {
      current.add(1, 'month');
      this.setDaysGrid(inputMode);
    } else if (view === DatePickerView.year) {
      current.add(1, 'year');
      this.setMonthsGrid(inputMode);
    } else {
      current.add(20, 'year');
      this.setYearsGrid(inputMode);
    }
  }

  goUp(inputMode?: InputMode): void {
    let viewKey = 'view';
    if (inputMode && inputMode !== InputMode.value) {
      viewKey = `${inputMode}View`;
    }

    if (this[viewKey] === DatePickerView.month) {
      this[viewKey] = DatePickerView.year;
      this.setMonthsGrid(inputMode);
    } else if (this[viewKey] === DatePickerView.year) {
      this[viewKey] = DatePickerView.years;
      this.setYearsGrid(inputMode);
    }
  }

  yearClick(option: SelectGridOption, inputMode?: InputMode): void {
    const currentKey = inputMode && inputMode !== InputMode.value ? `${inputMode}Current` : 'current';
    const viewKey = inputMode && inputMode !== InputMode.value ? `${inputMode}View` : 'view';

    this[currentKey].year(+option.label);
    this[viewKey] = DatePickerView.year;
    this.setMonthsGrid(inputMode);
    this.click();
  }

  monthClick(option: SelectGridOption, inputMode?: InputMode): void {
    const currentKey = inputMode && inputMode !== InputMode.value ? `${inputMode}Current` : 'current';
    const viewKey = inputMode && inputMode !== InputMode.value ? `${inputMode}View` : 'view';
    this[currentKey].month(option.index);
    this[viewKey] = DatePickerView.month;
    this.setDaysGrid(inputMode);
    this.click();
  }

  dayClick(option: SelectGridOption, inputMode?: InputMode): void {
    inputMode = inputMode || this.inputMode;

    const current = inputMode && inputMode !== InputMode.value ? this[`${inputMode}Current`] : this.current;
    current.month(option.month);
    current.date(+option.label);
    current.year(option.year);

    if (inputMode === InputMode.value) {
      this.error = false;
      this.value = moment(this.current);
      this.input = this.value.format(this.format);
      this.valueChange.emit(this.value);
      this.inputMode = undefined;
      this.isOpen = false;
    }

    if (inputMode === InputMode.end) {
      this.endValue = moment(this.endCurrent);
      this.endInput = this.endValue.format(this.format);
      this.endValueChange.emit(this.endValue);
      this.inputMode = undefined;
      this.isOpen = false;
    }

    if (inputMode === InputMode.start) {
      this.startValue = moment(this.startCurrent);
      this.startInput = this.startValue.format(this.format);
      this.startValueChange.emit(this.startValue);
      this.inputMode = InputMode.end;
    }

    if (this.rangeMode) {
      this.validateRange();
    }

    this.setDaysGrid();

    if (inputMode && inputMode !== InputMode.value) {
      this.setDaysGrid(inputMode);
      this.setDaysGrid(inputMode === InputMode.start ? InputMode.end : InputMode.start);
    }

    this.click();
  }

  toggleOpen(inputMode?: InputMode) {
    if (!this.disabled && !this.mouseUpEvent) {
      this.isOpenChange((this.isOpen && inputMode && this.inputMode === inputMode) || !this.isOpen);
    }
  }

  isOpenChange(open: boolean) {
    this.isOpen = open;
    if (!this.isOpen) {
      this.view = DatePickerView.month;
    }
  }

  mousedown() {
    if (this.isOpen) {
      this.mouseUpEvent = this.renderer.listen('document', 'mouseup', () => {
        this.mouseUpEvent();
        this.window.setTimeout(() => {
          this.mouseUpEvent = null;
        }, 0);
      });
    }
  }

  click($event?: MouseEvent): void {
    $event && $event.stopPropagation();
    this.element && this.element.nativeElement.click();
  }

  calendarIconClick($event?: MouseEvent) {
    if (!this.inline) {
      this.toggleOpen();
      this.parseInput();
      this.setInputMode(this.inputModes.value);
    }
    this.click($event);
  }

  rangeCalendarIconClick(mode?: InputMode, $event?: MouseEvent) {
    if (!this.inline) {
      const toggleMode = mode === InputMode.start ? InputMode.end : InputMode.start;
      this.toggleOpen(toggleMode);
      this.parseRangeInput(mode);
      this.setInputMode(mode);
    }
    this.click($event);
  }

  rangeContainerClasses = '';

  private _getRangeContainerClasses(): string {
    if (this.compactMode) return '';
    return 'date-picker__range-container flex--bottom'; // Add flex--bottom in case labels are multi-line.
  }

  rangeStartClasses = '';

  private _getRangeStartClasses(): string {
    if (this.compactMode) return 'wfm-date-picker__range-start-input__compact';
    if (this.autoWidth) return 'date-picker__range-start-input';
    return 'date-picker__range-start-input date-picker__fixed-width--range';
  }

  rangeStartInputClasses = '';

  private _getRangeStartInputClasses(): string {
    if (this.compactMode) return 'wfm-date-picker-outer-container__compact';
    return 'date-picker-outer-container date-picker-outer-container--range';
  }

  rangeEndClasses = '';

  private _getRangeEndClasses(): string {
    if (this.compactMode) return 'wfm-date-picker__range-end-input__compact';
    if (this.autoWidth) return 'date-picker__range-end-input';
    return 'date-picker__range-end-input date-picker__fixed-width--range';
  }

  rangeEndInputClasses = '';

  private _getRangeEndInputClasses(): string {
    if (this.compactMode) return 'wfm-date-picker-outer-container__compact';
    return 'date-picker-outer-container date-picker-outer-container--range';
  }

  valueInputClasses = '';

  private _getValueInputClasses(): string {
    if (this.compactMode) return 'wfm-date-picker-outer-container__compact';
    if (this.autoWidth) return 'date-picker-outer-container';
    return 'date-picker-outer-container date-picker__fixed-width';
  }

  isLeftArrowDisabled(inputMode?: InputMode): boolean {
    if (!this.min) return false;

    const current = inputMode && inputMode !== InputMode.value ? this[`${inputMode}Current`] : this.current;
    const view = inputMode && inputMode !== InputMode.value ? this[`${inputMode}View`] : this.view;
    const daysGrid = inputMode && inputMode !== InputMode.value ? this[`${inputMode}DaysGrid`] : this.daysGrid;
    const yearsGrid = inputMode && inputMode !== InputMode.value ? this[`${inputMode}YearsGrid`] : this.yearsGrid;
    const date = moment(current);

    if (view === DatePickerView.month) {
      // last day of previous month
      date
        .month(daysGrid[7].month) // second week is always current month
        .date(1) // first day of the month
        .subtract(1, 'day'); // previous day
    }

    if (view === DatePickerView.year) {
      // last day of previous year
      date.month(0).date(1).subtract(1, 'day');
    }

    if (view === DatePickerView.years) {
      // last day of previous year grid
      date
        .year(+yearsGrid[0].label)
        .month(0)
        .date(1) // first day of the month
        .subtract(1, 'day');
    }

    return moment(date.format('YYYY-MM-DD')).isBefore(this.min.format('YYYY-MM-DD'));
  }

  isRightArrowDisabled(inputMode?: InputMode): boolean {
    if (!this.max) return false;

    const current = inputMode && inputMode !== InputMode.value ? this[`${inputMode}Current`] : this.current;
    const view = inputMode && inputMode !== InputMode.value ? this[`${inputMode}View`] : this.view;
    const daysGrid = inputMode && inputMode !== InputMode.value ? this[`${inputMode}DaysGrid`] : this.daysGrid;
    const yearsGrid = inputMode && inputMode !== InputMode.value ? this[`${inputMode}YearsGrid`] : this.yearsGrid;
    const date = moment(current);

    if (view === DatePickerView.month) {
      // first day of next month
      date
        .month(daysGrid[7].month) // second week is always current month
        .date(1) // first day of the month
        .add(1, 'month');
    }

    if (view === DatePickerView.year) {
      // first day of next year
      date.month(0).date(1).add(1, 'year');
    }

    if (view === DatePickerView.years) {
      // first day of next year grid
      date
        .year(+yearsGrid[19].label)
        .month(0)
        .date(1) // first day of the month
        .add(1, 'year');
    }

    return moment(date.format('YYYY-MM-DD')).isAfter(this.max.format('YYYY-MM-DD'));
  }

  parseInput(type?: InputMode): void {
    const date = this.parse(this.input, true);

    this.error = false;

    // if there is no input
    // value is undefined and there is no error
    if (!this.input) {
      this.value = undefined;
    }

    // if input is provided but date is invalid
    // it is an error and value is undefined
    if (this.input && (!date.isValid() || !this.isInMinMaxDateLimits(date))) {
      this.error = true;
      this.value = null;
    }

    // if date is valid
    // update value and there is no error
    if (date.isValid() && !this.error) {
      this.value = moment(date);
      this.current = moment(date);
      this.input = this.value.format(this.format);
    }

    this.valueChange.emit(this.value);
    this.errorChange.emit(this.error);

    this.setDaysGrid(type);
  }

  parseRangeInput(type: InputMode): void {
    const date = this.parse(this[`${type}Input`], true);

    if (!date.isValid() || !this.isInMinMaxDateLimits(date)) {
      this[`${type}Value`] = undefined;
      this[`${type}Error`] = true;
    }

    if (date.isValid() && this.isInMinMaxDateLimits(date)) {
      this[`${type}Value`] = moment(date);
      this[`${type}Input`] = this[`${type}Value`].format(this.format);
      this[`${type}Error`] = false;
      this[`${type}ValueChange`].emit(this[`${type}Value`]);
    }

    this.validateRange(); // It is calling emitErrors()
    this.setDaysGrid();
  }
  private emitErrors() {
    if (this.rangeMode) {
      this.error = this.startError || this.endError;
      this.startErrorChange.emit(this.startError);
      this.endErrorChange.emit(this.endError);
    }
  }

  private parse(input: string, strict = false) {
    const date = moment(input, this.format, strict);
    const date2 = moment(input, this.format.replace(/[^a-zA-Z]/g, ''), strict);

    if (date2.isValid() && !date.isValid()) {
      return moment(date2.format(this.format), this.format, strict);
    }

    return date;
  }

  parseValue(type?: InputMode): void {
    if (type === InputMode.value) {
      type = undefined;
    }

    // Get the input value.
    const input = this[type ? `${type}Input` : 'input'];
    let error = false;

    // If there is valid input...
    if (input) {
      // logic for input 8 characters
      let strictDate;
      if (input.length === this.inputStrLen && input.split(this.formatDelimiter).length === 1) {
        const formatSplit = this.format.split(this.formatDelimiter);
        const formattedDateStr =
          input.slice(0, formatSplit[0].length) +
          this.formatDelimiter +
          input.slice(formatSplit[0].length, formatSplit[0].length + formatSplit[1].length) +
          this.formatDelimiter +
          input.slice(formatSplit[0].length + formatSplit[1].length);
        strictDate = this.parse(formattedDateStr, true);
        if (strictDate.isValid()) {
          this.input = formattedDateStr;
        }
      } else {
        // Try to parse the date.
        strictDate = this.parse(input, true);
      }

      // There is an error if no valid date is returned.
      if (!strictDate || !strictDate.isValid() || !this.isInMinMaxDateLimits(strictDate)) {
        error = true;
      }

      if (strictDate.isValid()) {
        if (this.rangeMode) {
          this.parseRangeInput(type);
        } else {
          this.parseInput();
        }
      }
    } else if (this.rangeMode) {
      this.parseRangeInput(type);
    }

    // Assign the error value.
    this[type ? `${type}Error` : 'error'] = error;
    this.emitErrors();
  }

  private isInMinMaxDateLimits(date: moment_.Moment) {
    if (this.max && moment(date.format('YYYY-MM-DD')).isAfter(this.max.format('YYYY-MM-DD'))) {
      return false;
    }

    if (this.min && moment(date.format('YYYY-MM-DD')).isBefore(this.min.format('YYYY-MM-DD'))) {
      return false;
    }

    return true;
  }

  private validateRange() {
    this.startError = this.endError = false;

    if (!(this.startInput && this.endInput)) {
      this.endError = !!this.startInput && !this.endInput;
      this.startError = !this.startInput && !!this.endInput;
    } else if (
      this.startValue &&
      this.endValue &&
      moment(this.startValue.format('YYYY-MM-DD')).isAfter(this.endValue.format('YYYY-MM-DD'))
    ) {
      // startValue after endValue
      this.startError = this.endError = true;
    }

    this.startError = this.startError || !!(!this.startValue && this.startInput);
    this.endError = this.endError || !!(!this.endValue && this.endInput);

    this.emitErrors();
  }

  private setYearsGrid(inputMode?: InputMode) {
    let current = this.current;
    let yearsGridKey = 'yearsGrid';
    if (inputMode && inputMode !== InputMode.value) {
      current = this[`${inputMode}Current`];
      yearsGridKey = `${inputMode}YearsGrid`;
    }
    const currentYear = current.format('YYYY');
    let decade: any = +currentYear[2];
    decade = `${decade - (decade % 2)}` + (+currentYear[3] ? 1 : 0);
    let year = +`${currentYear.substr(0, 2)}${decade}`;
    year = year % 10 ? year : year - 19;

    this[yearsGridKey] = [];
    this[yearsGridKey].length = 20;
    this[yearsGridKey].fill(null);
    this[yearsGridKey] = this[yearsGridKey].map((v, i) => {
      const opt = {
        label: `${year + i}`,
        selected: this.value && this.value.year() === year + i,
        disabled: false,
        emphasis: this.today.year() === year + i ? SelectGridEmphasis.high : SelectGridEmphasis.normal
      };

      const last = moment()
        .year(year + i)
        .month(11)
        .date(31);
      const first = moment()
        .year(year + i)
        .month(0)
        .date(1);

      opt.disabled = this.min && moment(last.format('YYYY-MM-DD')).isBefore(this.min.format('YYYY-MM-DD'));
      opt.disabled =
        opt.disabled || (this.max && moment(first.format('YYYY-MM-DD')).isAfter(this.max.format('YYYY-MM-DD')));

      return opt;
    });
  }

  private setMonthsGrid(inputMode?: InputMode) {
    let monthsGrid = this.monthsGrid;
    let current = this.current;
    let value = this.value;

    if (inputMode && inputMode !== InputMode.value) {
      monthsGrid = this[`${inputMode}MonthsGrid`];
      current = this[`${inputMode}Current`];
      value = this[`${inputMode}Value`];
    }

    for (const month of monthsGrid) {
      // last day of the month
      const last = moment(current).month(month.index).date(1).add(1, 'month').subtract(1, 'day');
      const first = moment(last).date(1);

      month.selected = value && value.year() === current.year() && value.month() === month.index;

      month.disabled = this.min && moment(last.format('YYYY-MM-DD')).isBefore(this.min.format('YYYY-MM-DD'));
      month.disabled =
        month.disabled || (this.max && moment(first.format('YYYY-MM-DD')).isAfter(this.max.format('YYYY-MM-DD')));

      month.emphasis = SelectGridEmphasis.normal;
      if (this.today.year() === current.year() && this.today.month() === month.index) {
        month.emphasis = SelectGridEmphasis.high;
      }
    }
  }

  private setDaysGrid(inputMode?: InputMode) {
    let current = this.current;
    let daysGridKey = 'daysGrid';

    if (inputMode && inputMode !== InputMode.value) {
      current = this[`${inputMode}Current`];
      daysGridKey = `${inputMode}DaysGrid`;
    }
    // first day of month
    const first = moment(current).date(1);

    // last day of month
    const last = moment(current).add(1, 'month').date(1).subtract(1, 'day');

    const preMonthVisibleDays = rotate([0, 1, 2, 3, 4, 5, 6], -this.$firstDayOfWeek)[first.day()];

    // total days considering visible days from previous month
    let size = last.date() + preMonthVisibleDays;

    // first visible day from previous month
    first.subtract(preMonthVisibleDays, 'day');

    // if visible days not multiple of 7, add next month visible days
    if (size % 7) {
      last.add(7 - (size % 7), 'day');
      size += 7 - (size % 7);
    }

    this[daysGridKey] = [];

    const day = first;

    for (let i = 0; i < size; i++) {
      const opt: SelectGridOption = {
        label: `${day.date()}`,
        selected:
          this.value &&
          this.value.year() === day.year() &&
          this.value.month() === day.month() &&
          this.value.date() === day.date(),
        disabled: false,
        emphasis: SelectGridEmphasis.normal,
        month: day.month(),
        year: day.year()
      };

      if (this.rangeMode) {
        opt.selected = this.startValue && moment(this.startValue.format('YYYY-MM-DD')).isSame(day.format('YYYY-MM-DD'));
        opt.selected =
          opt.selected ||
          (this.endValue && moment(this.endValue.format('YYYY-MM-DD')).isSame(day.format('YYYY-MM-DD')));

        opt.range =
          this.startValue &&
          this.endValue &&
          moment(day.format('YYYY-MM-DD')).isBetween(
            this.startValue.format('YYYY-MM-DD'),
            this.endValue.format('YYYY-MM-DD')
          );
      }

      opt.disabled = this.min && moment(day.format('YYYY-MM-DD')).isBefore(this.min.format('YYYY-MM-DD'));
      opt.disabled =
        opt.disabled || (this.max && moment(day.format('YYYY-MM-DD')).isAfter(this.max.format('YYYY-MM-DD')));

      if (this.filter) {
        opt.disabled = !this.filter(day) || opt.disabled;
      }

      if (day.month() !== current.month()) {
        opt.emphasis = SelectGridEmphasis.low;
        opt.label = (!this.hideOtherMonths && opt.label) || '';
      } else if (
        this.today.year() === day.year() &&
        this.today.month() === day.month() &&
        this.today.date() === day.date()
      ) {
        opt.emphasis = SelectGridEmphasis.high;
      }

      this[daysGridKey].push(opt);
      day.add(1, 'day');
    }
  }
}

/**
 * 20200715
 * 20201229
 * YYYY-MM-DD
 */
